import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import {
  MAX_FILESTACK_FILE_SIZE,
  MSOfficeMimeType,
  ONE_HOUR_MS,
  OtherMimeType,
} from '@aline/common';
import type { ApolloLink } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Filestack, PickerDisplayMode } from 'filestack-js';
import type {
  Client,
  ClientOptions,
  PickerFileMetadata,
  PickerInstance,
  PickerOptions,
  PickerUploadDoneCallback,
} from 'filestack-js';
import type { PickerFileCallback } from 'filestack-js/build/main/lib/picker';
import { zipWith } from 'ramda';
import { useEvent } from 'react-use';
import { filestackApiKey, supportEmail } from 'src/config';
import { Apollo } from 'src/gql/generated';
import {
  resolveDocxUploadTarget,
  useDocxUploadDocumentLookup,
} from 'src/helpers/docx-upload-target';
import type { UploadAsLatestVersionChoice } from 'src/helpers/docx-upload-target';
import { doNothing } from 'src/helpers/utils';
import { useCreateFiles, useCreateStaticFileVersion, useCreateStaticFileVersions } from 'src/hooks';
import { Sentry } from 'src/sentry';

import { filestackS3StoreOptions } from './filestack-multiupload';

export enum FileStackDomContainer {
  ChatContext = 'chat-context-picker',
  FileVersion = 'file-version-picker',
  Image = 'image-picker',
  LaunchFormDocument = 'launch-form-document-picker',
  LaunchTemplate = 'launch-template-picker',
  OrgAvatar = 'org-avatar-picker',
  PartyDocuments = 'party-documents-picker',
  Repository = 'repository-picker',
  SendForSignaturePage = 'send-for-signature-page-picker',
}
export const pdfMimeTypes = [OtherMimeType.PDF] as const;
export const wordMimeTypes = [
  MSOfficeMimeType.DOC,
  MSOfficeMimeType.DOCX,
  MSOfficeMimeType.COMPOUND_FILE,
] as const;
export const pptMimeTypes = [MSOfficeMimeType.PPT, MSOfficeMimeType.PPTX] as const;
export const excelMimeTypes = [MSOfficeMimeType.XLS, MSOfficeMimeType.XLSX] as const;
export const imageMimeTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
/** Image formats that can be inserted into a document. Matches the formats docx export supports. */
export const editorImageMimeTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/bmp'] as const;
// All accepted MIME types for file uploads.
export const acceptedMimeTypes = new Set<string>([
  ...pdfMimeTypes,
  ...wordMimeTypes,
  ...pptMimeTypes,
  ...excelMimeTypes,
  OtherMimeType.TEXT_PLAIN,
  OtherMimeType.CSV,
]);
// AI Chat accepts Word and PDF files.
export const aiChatAcceptedMimeTypes = new Set<string>([...pdfMimeTypes, ...wordMimeTypes]);

/** MIME type prefixes to accept (e.g., all text/* or image/* types) */
export const acceptedMimeTypePrefixes = ['text/', 'image/'];
/** Default accepted formats for Filestack picker (MIME types + wildcards) */
export const defaultAcceptedFormats = [...acceptedMimeTypes, 'text/*', 'image/*'];

/* TODO: Add in additional integrations commented out below.  The additional integrations require a custom OAUTH app to work.  Ticket - https://arity.atlassian.net/browse/AR-648. */
const fromSources = [
  'local_file_system',
  // 'url',
  'googledrive',
  'onedrive',
  'box',
  // 'dropbox',
  // 'onedriveforbusiness'
];

const defaultMaxFiles = 500;
const maxSize = MAX_FILESTACK_FILE_SIZE;

interface PickerOptionArgs {
  closeDialog: () => void;
  container: string;
  accept?: string[];
  maxFiles?: number;
  onFileUploadFinished?: PickerFileCallback;
  onUploadDone?: PickerUploadDoneCallback;
}

function createPickerOptions(args: PickerOptionArgs): Partial<PickerOptions> {
  const {
    accept = defaultAcceptedFormats,
    closeDialog,
    container,
    maxFiles = defaultMaxFiles,
    onFileUploadFinished,
    onUploadDone,
  } = args;

  return {
    accept,
    container,
    displayMode: PickerDisplayMode.inline,
    exposeOriginalFile: true,
    fromSources,
    maxFiles,
    maxSize,
    onClose: closeDialog,
    onFileUploadFailed: reportPickerUploadFailure,
    onFileUploadFinished,
    onUploadDone,
    supportEmail,
  };
}

/**
 * The Filestack picker labels every failed upload "File not found" regardless
 * of the underlying cause (expired security policy, network error, etc.), so
 * the real error is invisible in the UI. Capture it here so production
 * failures are diagnosable (see ENG-5155).
 */
function reportPickerUploadFailure(file: PickerFileMetadata, error: Error): void {
  Sentry.captureException(error, {
    extra: {
      filename: file.filename,
      mimetype: file.mimetype,
      size: file.size,
      source: file.source,
    },
    tags: { feature: 'filestack-picker' },
  });
}

const FilestackContext = React.createContext<Client>(Filestack(filestackApiKey));

export function useFilestackClient(): Client {
  return useContext(FilestackContext);
}

function createFilestackClient(options: Apollo.FilestackUploadOptions | null): Client {
  const clientOptions: ClientOptions = {
    bucket: options?.bucket,
    security: options?.security,
    storeTo: {
      container: options?.bucket,
      ...filestackS3StoreOptions,
    },
  };

  return Filestack(filestackApiKey, clientOptions);
}

export function FilestackClientProvider({
  children,
  client,
}: {
  children: React.ReactNode;
  client: Client;
}): JSX.Element {
  return <FilestackContext.Provider value={client}>{children}</FilestackContext.Provider>;
}

/**
 * Builds a Filestack client from signed upload-security options and keeps them
 * fresh. Callers poll their GraphQL query hourly; this hook also refetches when
 * a backgrounded tab becomes visible again and the cached policy may be stale
 * (browsers throttle timers in background tabs — see ENG-5155).
 */
function useFilestackClientFromUploadSecurity({
  loading = false,
  refetch,
  refreshIntervalMs = ONE_HOUR_MS,
  uploadSecurity,
}: {
  refetch: () => Promise<unknown>;
  uploadSecurity: Apollo.FilestackUploadOptions | null;
  loading?: boolean;
  refreshIntervalMs?: number;
}): { filestackClient: Client; loading: boolean } {
  /**
   * Authenticated Filestack security policies expire after 4 hours, public
   * launch-config ones after 30 minutes (see FilestackService in the server's
   * filestack utils). Without polling, the policy is fetched once per session
   * and every upload after expiry fails — surfaced by the picker as a generic
   * "File not found" (ENG-5155). `refreshIntervalMs` must stay well under the
   * expiry the caller's query mints, while keeping client (and therefore
   * picker) recreation infrequent.
   */
  const lastFetchedAtRef = useRef(Date.now());
  const filestackClient = useMemo(() => createFilestackClient(uploadSecurity), [uploadSecurity]);

  useEffect(() => {
    lastFetchedAtRef.current = Date.now();
  }, [uploadSecurity]);

  const refreshUploadSecurityIfStale = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastFetchedAtRef.current < refreshIntervalMs) return;

    lastFetchedAtRef.current = Date.now();
    void refetch();
  }, [refetch, refreshIntervalMs]);

  /**
   * Browsers throttle timers in background tabs, so the hourly poll can be skipped entirely while
   * a tab sleeps. Refetch on visibility regain when the security is older than the poll cadence,
   * so a picker opened right after waking the tab gets fresh credentials.
   */
  useEvent('visibilitychange', refreshUploadSecurityIfStale, document);

  return { filestackClient, loading };
}

export function useCreateFilestackClient(): { filestackClient: Client; loading: boolean } {
  const { data, loading, refetch } = Apollo.useGetUploadSecurityQuery({
    pollInterval: ONE_HOUR_MS,
  });

  return useFilestackClientFromUploadSecurity({
    loading,
    refetch,
    uploadSecurity: data?.uploadSecurity ?? null,
  });
}

/**
 * Returns a callback that mints a Filestack client from freshly fetched upload security,
 * bypassing the Apollo cache. Use at upload start (and for one retry after a policy/auth upload
 * failure) so uploads never begin with credentials that expired while cached.
 */
export function useGetFreshFilestackClient(): () => Promise<Client> {
  const apolloClient = useApolloClient();

  return useCallback(async () => {
    const { data } = await apolloClient.query<Apollo.GetUploadSecurityQuery>({
      fetchPolicy: 'network-only',
      query: Apollo.GetUploadSecurityDocument,
    });

    return createFilestackClient(data?.uploadSecurity ?? null);
  }, [apolloClient]);
}

export function extractFileName(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1];
}

export type AfterImageUploadDoneResult =
  Apollo.CreateFileLocationsMutation['createFileLocations'][number] & {
    height: number;
    width: number;
  };

export interface CreateImageUploadFilesPickerOpts {
  afterUploadDone: (data: AfterImageUploadDoneResult[]) => void;
  onClose: () => void;
  accept?: string[];
  initialFiles?: Blob[];
  maxFiles?: number;
}

export function useCreateImageUploadFilesPicker(
  domContainer: string,
  options: CreateImageUploadFilesPickerOpts
): {
  picker: PickerInstance;
} {
  const { accept, afterUploadDone, maxFiles, onClose } = options;
  const filestackClient = useFilestackClient();
  const propsRef = useRef({ accept, afterUploadDone, onClose });
  const [createFileLocations] = Apollo.useCreateFileLocationsMutation();

  useEffect(() => {
    propsRef.current = { accept, afterUploadDone, onClose };
  }, [afterUploadDone, onClose, accept]);

  const onUploadDone: PickerUploadDoneCallback = useCallback(
    async ({ filesUploaded }) => {
      const records = filesUploaded.map(file => {
        const { container, filename, handle, key, mimetype, size, url } = file;
        if (!container || !key)
          throw new Error(`Uploaded file missing data - ${JSON.stringify(file)}.`);

        return {
          container,
          filename: extractFileName(filename),
          handle,
          key,
          mimetype,
          size,
          url,
        };
      });
      if (!records.length) return null;

      const { data } = await createFileLocations({
        variables: {
          input: {
            records,
          },
        },
      });
      if (!data) return null;

      const filesMetadata = await Promise.all(
        records.map(async file => {
          const { handle } = file;

          return filestackClient.metadata(handle, {
            height: true,
            width: true,
          });
        })
      );
      const response = zipWith(
        (fileLocation, metadata): AfterImageUploadDoneResult => {
          const { height, width } = metadata;
          return { ...fileLocation, height, width };
        },
        data.createFileLocations,
        filesMetadata
      );

      propsRef.current.onClose();
      propsRef.current.afterUploadDone?.(response);
    },
    [createFileLocations, filestackClient]
  );
  const picker = useMemo(() => {
    return filestackClient.picker(
      createPickerOptions({
        accept: propsRef.current.accept,
        closeDialog: propsRef.current.onClose,
        container: domContainer,
        maxFiles,
        onUploadDone,
      })
    );
  }, [domContainer, filestackClient, maxFiles, onUploadDone]);

  return { picker };
}

export interface CreateUploadFilesPickerOpts {
  confirmUploadAsLatestVersion: (documentName: string) => Promise<UploadAsLatestVersionChoice>;
  onClose: () => void;
  props: {
    documentVariant: Apollo.DocumentVariant;
    isSigned: boolean;
    partyIds?: UUID[];
  };
  accept?: string[];
  afterUploadDone?: (data: Awaited<Promise<ApolloLink.Result<Apollo.CreateFilesMutation>>>) => void;
  maxFiles?: number;
  reportId?: UUID;
}

export function useCreateUploadFilesPicker(
  domContainer: string,
  options: CreateUploadFilesPickerOpts
): {
  picker: PickerInstance;
} {
  const {
    accept,
    afterUploadDone = doNothing,
    confirmUploadAsLatestVersion,
    maxFiles,
    onClose,
    props,
    reportId,
  } = options;
  const { createFiles } = useCreateFiles();
  const { createStaticFileVersions } = useCreateStaticFileVersions();
  const filestackClient = useFilestackClient();
  const lookupDocument = useDocxUploadDocumentLookup();

  const propsRef = useRef({
    accept,
    afterUploadDone,
    confirmUploadAsLatestVersion,
    createStaticFileVersions,
    documentVariant: props.documentVariant,
    isSigned: props.isSigned,
    lookupDocument,
    maxFiles,
    onClose,
    partyIds: props.partyIds,
    reportId,
  });

  /**
   * This is a tricky issue, but the filestack picker is not updating the onUploadDone callback
   * when these props change.  This is a workaround to ensure that the onUploadDone callback has the right
   * props and the filestack picker doesn't need to be recreated.
   */
  useEffect(() => {
    propsRef.current = {
      accept,
      afterUploadDone,
      confirmUploadAsLatestVersion,
      createStaticFileVersions,
      documentVariant: props.documentVariant,
      isSigned: props.isSigned,
      lookupDocument,
      maxFiles,
      onClose,
      partyIds: props.partyIds,
      reportId,
    };
  }, [
    accept,
    afterUploadDone,
    confirmUploadAsLatestVersion,
    createStaticFileVersions,
    lookupDocument,
    maxFiles,
    onClose,
    props.documentVariant,
    props.isSigned,
    props.partyIds,
    reportId,
  ]);

  const onUploadDone: PickerUploadDoneCallback = useCallback(
    async ({ filesUploaded }) => {
      const newDocumentFiles: PickerFileMetadata[] = [];
      const versionUploads: { documentId: UUID; file: PickerFileMetadata }[] = [];

      for (const file of filesUploaded) {
        const originalFile = file.originalFile;
        const bytes = originalFile instanceof Blob ? await originalFile.arrayBuffer() : undefined;
        const target = await resolveDocxUploadTarget({
          bytes,
          filename: file.filename,
          lookupDocument: propsRef.current.lookupDocument,
          mimetype: file.mimetype,
          url: file.url,
        });
        if (target.kind === 'versionCandidate') {
          const choice = await propsRef.current.confirmUploadAsLatestVersion(target.document.name);
          if (choice === 'asVersion') {
            versionUploads.push({ documentId: target.document.id, file });
            continue;
          }
        }
        newDocumentFiles.push(file);
      }

      if (versionUploads.length) {
        try {
          const { errors } = await propsRef.current.createStaticFileVersions({
            variables: {
              input: {
                records: versionUploads.map(({ documentId, file }) => {
                  const { fileLocationProps } = toCreateFilesRecord(file, {
                    documentVariant: propsRef.current.documentVariant,
                    isSigned: propsRef.current.isSigned,
                  });
                  return {
                    documentId,
                    props: { fileLocation: fileLocationProps },
                  };
                }),
              },
            },
          });

          const firstError = errors?.at(0);
          if (firstError) throw firstError;
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'filestack-picker' },
          });
        }
      }

      if (!newDocumentFiles.length) {
        propsRef.current.onClose();
        return;
      }

      const res = await createFiles({
        reportId: propsRef.current.reportId,
        variables: {
          input: {
            partyIds: propsRef.current.partyIds,
            records: newDocumentFiles.map(file =>
              toCreateFilesRecord(file, {
                documentVariant: propsRef.current.documentVariant,
                isSigned: propsRef.current.isSigned,
              })
            ),
          },
        },
      });

      await propsRef.current.afterUploadDone(res);
    },
    [createFiles]
  );
  const picker = useMemo(() => {
    return filestackClient.picker(
      createPickerOptions({
        accept: propsRef.current.accept,
        closeDialog: propsRef.current.onClose,
        container: domContainer,
        maxFiles: propsRef.current.maxFiles,
        onUploadDone,
      })
    );
  }, [domContainer, filestackClient, onUploadDone]);

  return { picker };
}

export interface CreateFileVersionPickerOpts {
  documentId: UUID;
  onClose: () => void;
  accept?: string[];
  afterUpload?: (
    fileVersion: Pick<Apollo.FileVersion, 'id' | '__typename' | 'name'>
  ) => Promise<void>;
  /**
   * When set, used instead of createStaticFileVersion after Filestack finishes
   * (e.g. Sign Outside applies the signed version via a workflow mutation).
   */
  createFromUpload?: (
    fileLocation: Apollo.FileLocationProps
  ) => Promise<Pick<Apollo.FileVersion, 'id' | '__typename' | 'name'> | null | undefined>;
}

export function useCreateFileVersionPicker(
  domContainer: string,
  options: CreateFileVersionPickerOpts
): {
  picker: PickerInstance;
} {
  const filestackClient = useFilestackClient();
  const {
    accept = [...pdfMimeTypes, ...wordMimeTypes],
    afterUpload = doNothing,
    createFromUpload,
    documentId,
    onClose,
  } = options;
  const { createStaticFileVersion } = useCreateStaticFileVersion();
  const propsRef = useRef({ accept, afterUpload, onClose });
  propsRef.current = { accept, afterUpload, onClose };

  const createFileVersion = useCallback(
    async (fileLocation: Apollo.FileLocationProps) => {
      if (createFromUpload) return createFromUpload(fileLocation);

      const { data } = await createStaticFileVersion(documentId, {
        variables: {
          input: {
            documentId,
            props: { fileLocation },
          },
        },
      });
      return data?.createStaticFileVersion.fileVersion;
    },
    [createFromUpload, createStaticFileVersion, documentId]
  );

  const onFileUploadFinished: PickerFileCallback = useCallback(
    async file => {
      const { container, filename, handle, key, mimetype, size, url } = file;
      if (!container || !key)
        throw new Error(`Uploaded file missing data - ${JSON.stringify(file)}.`);

      const fileVersion = await createFileVersion({
        container,
        filename: extractFileName(filename),
        handle,
        key,
        mimetype,
        size,
        url,
      });
      if (fileVersion) await propsRef.current.afterUpload?.(fileVersion);
      propsRef.current.onClose();
    },
    [createFileVersion]
  );
  const picker = useMemo(() => {
    return filestackClient.picker(
      createPickerOptions({
        accept: propsRef.current.accept,
        closeDialog: propsRef.current.onClose,
        container: domContainer,
        maxFiles: 1,
        onFileUploadFinished,
      })
    );
  }, [domContainer, filestackClient, onFileUploadFinished]);

  return { picker };
}

export interface CreateOrgAvatarPickerOpts {
  onClose: () => void;
  afterUpload?: (org: Pick<Apollo.Org, 'id' | '__typename'>) => Promise<void>;
}

export function useCreateOrgAvatarPicker(
  domContainer: string,
  options: CreateOrgAvatarPickerOpts
): {
  picker: PickerInstance;
} {
  const filestackClient = useFilestackClient();
  const { afterUpload = doNothing, onClose } = options;
  const propsRef = useRef({ afterUpload, onClose });
  const [upsertOrgAvatarMutation] = Apollo.useUpsertOrgAvatarImgMutation();

  useEffect(() => {
    propsRef.current = { afterUpload, onClose };
  }, [afterUpload, onClose]);

  const onFileUploadFinished: PickerFileCallback = useCallback(
    async file => {
      const { container, filename, handle, key, mimetype, size, url } = file;
      if (!container || !key)
        throw new Error(`Uploaded file missing data - ${JSON.stringify(file)}.`);

      const { data } = await upsertOrgAvatarMutation({
        variables: {
          input: {
            container,
            filename: extractFileName(filename),
            handle,
            key,
            mimetype,
            size,
            url,
          },
        },
      });
      if (data) await propsRef.current.afterUpload(data.upsertOrgAvatarImg.org);
      propsRef.current.onClose();
    },
    [upsertOrgAvatarMutation]
  );
  const picker = useMemo(() => {
    return filestackClient.picker(
      createPickerOptions({
        accept: [...imageMimeTypes],
        closeDialog: propsRef.current.onClose,
        container: domContainer,
        maxFiles: 1,
        onFileUploadFinished,
      })
    );
  }, [domContainer, filestackClient, onFileUploadFinished]);

  return { picker };
}

export function FileStackProvider({ children }: { children: JSX.Element }): JSX.Element {
  const { filestackClient } = useCreateFilestackClient();

  return <FilestackClientProvider client={filestackClient}>{children}</FilestackClientProvider>;
}

/** Public launch policies expire after 30 minutes; refresh at half that to keep a margin. */
const PUBLIC_UPLOAD_SECURITY_REFRESH_MS = 15 * 60 * 1000;

/**
 * Filestack client for the unauthenticated public launch page. Uses
 * launch-config-scoped upload security instead of the auth-only
 * `uploadSecurity` query.
 */
export function LaunchConfigFileStackProvider({
  children,
  launchConfigId,
}: {
  children: React.ReactNode;
  launchConfigId: UUID;
}): JSX.Element {
  const { data, refetch } = Apollo.useGetUploadSecurityViaLaunchConfigQuery({
    pollInterval: PUBLIC_UPLOAD_SECURITY_REFRESH_MS,
    variables: { launchConfigId },
  });
  const { filestackClient } = useFilestackClientFromUploadSecurity({
    refetch,
    refreshIntervalMs: PUBLIC_UPLOAD_SECURITY_REFRESH_MS,
    uploadSecurity: data?.uploadSecurityViaLaunchConfig ?? null,
  });

  return <FilestackClientProvider client={filestackClient}>{children}</FilestackClientProvider>;
}

function toCreateFilesRecord(
  file: PickerFileMetadata,
  options: { documentVariant: Apollo.DocumentVariant; isSigned: boolean }
) {
  const { container, filename, handle, key, mimetype, size, url } = file;
  if (!container || !key) throw new Error(`Uploaded file missing data - ${JSON.stringify(file)}.`);

  return {
    documentProps: {
      isSigned: options.isSigned,
      variant: options.documentVariant,
    },
    fileLocationProps: {
      container,
      filename: extractFileName(filename),
      handle,
      key,
      mimetype,
      size,
      url,
    },
  };
}
